import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, Cloud, Repeat, ArrowUpRight, ArrowRight, ArrowDown, Sparkles, Target, Users2, LineChart, GraduationCap, CheckCircle2, Lightbulb, Puzzle, Beaker, Wrench, Brain, Activity, Layers, Ruler, Calculator, Search } from "lucide-react";
import { PageHero, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { IMG } from "@/data/content";

const COPE_LEVELS = [
  { lvl: "Level 1", t: "Foundation & Curriculum", d: "Curriculum-level problems that build conceptual understanding and first application." },
  { lvl: "Level 2", t: "Advanced & Analytical", d: "Higher-order problems requiring the integration of multiple concepts." },
  { lvl: "Level 3", t: "Olympiad & Research", d: "International Olympiad, advanced-entrance and real-world innovation-level challenges." },
];

const COPE_AUDIENCE = [
  { icon: GraduationCap, t: "For School Leaders", d: "Real-time monitoring of PBL-TDLE implementation — evidence-based academic quality assurance." },
  { icon: Users2, t: "For Students & Parents", d: "Daily concept-mastery reports, formative grades on every problem and experiment, and topic-wise analytics." },
  { icon: LineChart, t: "For Teachers", d: "A complete, structured, data-driven framework: concepts, demonstrations, experiments and apparatus for every subtopic." },
];

// 8-step central learning sequence (moved from Concept Labs page)
const CYCLE_STEPS = [
  { icon: Brain,        hex: "#8B5CF6", title: "AI Diagnostic Test",                          d: "An AI-based diagnostic test maps the target concepts against the cohort — pinpointing exactly what needs to be developed for this class." },
  { icon: Cloud,        hex: "#2E6BB0", title: "CoPE-App Generates a Tailor-Made Lesson Plan", d: "The cloud-based CoPE-App produces a digital, tailor-made lesson plan — a curated list of concepts, problems, experiments and apparatus for the day." },
  { icon: Puzzle,       hex: "#F0A831", title: "Teacher Displays the Problem",                d: "The lesson opens with an unfamiliar situation — a real problem on the topic to be taught, displayed to the whole class." },
  { icon: Search,       hex: "#1E9E8A", title: "Students Analyse the Problem",                d: "Students break it down themselves — identifying the inputs, the outputs, and the concepts they don't yet know." },
  { icon: Sparkles,     hex: "#E14C7A", title: "Teacher Demonstrates Every Concept Live",     d: "Using the CoPE-App concept list and the students' own analysis, the teacher demonstrates each concept with the designed experiments and apparatus — making measurements, graphing, doing data analysis and verifying every concept in front of the class." },
  { icon: Beaker,       hex: "#1E9E8A", title: "Students Investigate & Verify Independently", d: "Each student then repeats the investigation — verifying every concept for themselves with the apparatus, while the teacher moves through the lab guiding, watching and confirming that every learner has actually verified every concept." },
  { icon: Target,       hex: "#F0A831", title: "Students Apply Concepts to the Problem",      d: "With every concept now genuinely developed, students return to the original problem and apply their developed concepts to solve it." },
  { icon: CheckCircle2, hex: "#8B5CF6", title: "Solution Displayed · Students Self-Correct",  d: "The teacher displays the solution. Every student cross-checks their own work against it, identifies gaps, and corrects them on the spot." },
];


// CoPE-App — the four letters (rebuilt from the source posters, in-theme)
const COPE_LETTERS = [
  {
    letter: "Co",
    key: "concepts",
    tag: "C — Concepts",
    icon: Lightbulb,
    hex: "#2E6BB0",
    title: "Concepts",
    subtitle: "Build strong conceptual foundations",
    body: "Concepts are the ideas, laws and principles of science and mathematics. They must be developed — not merely known — because they must be applied in problem solving. For every topic, unit and chapter, the required concepts are identified from the curriculum prescribed by the board and included in the CoPE-App.",
    steps: [
      { n: "01", t: "Demonstrate", d: "See the concept in action" },
      { n: "02", t: "Investigate", d: "Explore it through experiments" },
      { n: "03", t: "Verify", d: "Observe, measure and confirm" },
      { n: "04", t: "Connect", d: "Link concepts to real life" },
    ],
    ribbon: "Concept mastery, not memorisation",
  },
  {
    letter: "P",
    key: "problems",
    tag: "P — Problems",
    icon: Puzzle,
    hex: "#F0A831",
    title: "Real-life, open-ended problems",
    subtitle: "Develop analytical thinking & problem solving",
    body: "Problem solving is the process of applying concepts to find solutions to new and unfamiliar situations. The CoPE-App's problem bank of 10,000+ newly created, real-life, open-ended problems trains students to apply concepts. Each problem is engineered around the concepts to be developed and applied in that specific topic.",
    steps: [
      { n: "01", t: "Identify", d: "Understand the problem and given information" },
      { n: "02", t: "Connect", d: "Identify and connect the required concepts" },
      { n: "03", t: "Apply",   d: "Plan and execute a logical solution" },
      { n: "04", t: "Explain", d: "Present, verify and improve the solution" },
    ],
    ribbon: "Apply concepts, not memorised answers",
  },
  {
    letter: "E",
    key: "experiments",
    tag: "E — Experiments",
    icon: Beaker,
    hex: "#1E9E8A",
    title: "Experiments for investigation & verification",
    subtitle: "Learn by experimenting — observe, measure, verify",
    body: "Experiments let students investigate, observe, measure and verify every concept through direct experience. Thousands of experiments — designed by research scientists — develop every concept for learners aged 5 to 18 across Physics, Chemistry, Biology and Mathematics.",
    steps: [
      { n: "01", t: "Observe",  d: "Study phenomena carefully" },
      { n: "02", t: "Measure",  d: "Collect accurate scientific data" },
      { n: "03", t: "Analyse",  d: "Interpret results, graphs and patterns" },
      { n: "04", t: "Verify",   d: "Confirm and master the concept" },
    ],
    ribbon: "Every concept developed through experience",
  },
  {
    letter: "App",
    key: "apparatus",
    tag: "App — Apparatus",
    icon: Wrench,
    hex: "#8B5CF6",
    title: "Global-standard scientific apparatus",
    subtitle: "Every classroom becomes a Concept Lab",
    body: "Apparatus transform every classroom into a Concept Lab. The CoPE-App integrates 1,500+ globally standardised scientific apparatus that develop concepts across Physics, Chemistry, Biology and Mathematics — turning ordinary rooms into fully equipped labs.",
    steps: [
      { n: "01", t: "Access",     d: "Apparatus available in every classroom" },
      { n: "02", t: "Experiment", d: "Perform hands-on investigations" },
      { n: "03", t: "Measure",    d: "Collect accurate and reliable data" },
      { n: "04", t: "Verify",     d: "Develop and master every concept" },
    ],
    ribbon: "Every classroom is a Concept Lab",
  },
];

const COPE_SUBJECTS = ["Physics", "Chemistry", "Biology", "Mathematics"];
const COPE_GRADES = ["G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8", "G9", "G10", "G11", "G12"];

const AI_DIAGNOSTIC_CAPS = [
  { icon: Brain,     hex: "#2E6BB0", t: "Concept-level diagnostics",     d: "The AI engine maps every student's understanding of every concept before teaching begins — surfacing strengths, gaps and misconceptions at the cohort level." },
  { icon: Activity,  hex: "#1E9E8A", t: "Real-time formative assessment", d: "Every problem, every experiment and every response is logged. Mastery of each subtopic is scored continuously — not left to end-of-term exams." },
  { icon: Target,    hex: "#F0A831", t: "Personalised lesson planning",   d: "The next lesson is generated per cohort — reinforcing weak concepts, escalating strong ones — so the teacher walks in with a plan the AI has already tailored." },
  { icon: LineChart, hex: "#8B5CF6", t: "Transparent dashboards",         d: "School leaders, teachers, parents and students all see the same signal: which concepts are mastered, which are not, and what will be worked on next." },
];

// Concept Labs — real photographs of students performing experiments
// Sourced from thecreatoreducation.com/concept-labs (WhatsApp-image files are real student photos)
const CONCEPT_LAB_IMG = "https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=1024,fit=crop/OyPMhZFo366K6YNr/";
const CONCEPT_LAB_PHOTOS = [
  { src: CONCEPT_LAB_IMG + "whatsapp-image-2022-08-04-at-8.10.38-am-NqlWE7DnELwKqek7.jpeg", label: "Chemistry — hands-on investigation" },
  { src: CONCEPT_LAB_IMG + "20180120_184318-QYyQGVPKKOOdparB.jpg",                          label: "Physics — apparatus-based experiment" },
  { src: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/bkfdzytl_Screenshot%202026-01-31%20155607.png", label: "Students recording measurements" },
  { src: CONCEPT_LAB_IMG + "whatsapp-image-2026-05-03-at-10.54.56-pm-1-Ly6XrN2pTfWTwiYA.jpeg", label: "Concept Lab bench work" },
];


const PILLARS = [
  { key: "labs", icon: FlaskConical, title: "Concept Labs", short: "Modified classrooms", body: "Several hundred globally standardized apparatus and several thousand research-designed experiments. Every subtopic is taught through demonstration, investigation and experimental verification — ensuring deep concept mastery rather than passive learning.", img: IMG.lab1 },
  { key: "cope", icon: Cloud, title: "CoPE-App", short: "Cloud academic engine", body: "A cloud-based academic engine housing Concepts, Problems, Experiments and Apparatus. A tailor-made lesson-planning and execution system based on cohort-level conceptual diagnostics, driving daily problem solving and real-time formative assessment.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/l9kdbyl3_CoPE-App.webp" },
  { key: "idea", icon: Repeat, title: "IDEA Pedagogy", short: "Identify → Develop → Execute → Assess", body: "A continuous pedagogical loop — Identification, Development, Execution, Assessment — that ensures learning is continuously assessed, corrected and strengthened in real time.", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/um9o9vm9_CoPE-App%202.webp" },
];

const IDEA = [
  {
    n: "I",
    t: "Identify the Problem",
    lede: "Learning begins with a real-life, open-ended problem displayed by the teacher. Students analyse it and scientifically identify three things.",
    bullets: [
      { h: "Given Inputs",       d: "The information, values, conditions and data already provided." },
      { h: "Desired Outcome",    d: "The result, target or solution the problem is asking for." },
      { h: "Unknown Concepts",   d: "The principles, formulas and scientific ideas required to solve it." },
    ],
    outcome: "Builds observation, analytical thinking, questioning and scientific reasoning.",
  },
  {
    n: "D",
    t: "Develop the Concepts",
    lede: "Concepts are developed in two stages — first live demonstrations by the teacher, then hands-on experiments by every student.",
    bullets: [
      { h: "Demonstration by Teachers", d: "Live demos with global-standard apparatus, models and simulations — replacing chalk-and-talk with visible cause-and-effect." },
      { h: "Experiments by Students",   d: "Every student measures, records, plots graphs, analyses errors and draws conclusions — learning is scientifically verified, not assumed." },
    ],
    outcome: "Concept mastery through investigation, evidence and experience.",
  },
  {
    n: "E",
    t: "Execute the Solution",
    lede: "With concepts developed and verified, students combine several concepts to solve the original problem end-to-end.",
    bullets: [
      { h: "Plan Systematically",         d: "Break the problem down and design a logical approach." },
      { h: "Apply Multiple Concepts",     d: "Connect scientific and mathematical ideas across the problem." },
      { h: "Solve Open-Ended Problems",   d: "Move beyond textbook drills to real-life, innovation-level challenges." },
    ],
    outcome: "Deep problem-solving ability, conceptual application, creativity and confidence.",
  },
  {
    n: "A",
    t: "Assess the Solution",
    lede: "The teacher displays the model solution; every student compares, spots gaps and self-corrects on the spot.",
    bullets: [
      { h: "Compare & Reflect",       d: "Students match their solution against the model and locate mistakes or misconceptions." },
      { h: "Correct Immediately",     d: "Errors are fixed in the same lesson — not weeks later in a test." },
      { h: "Strengthen & Master",     d: "Self-assessment builds mastery through continuous, real-time improvement." },
    ],
    outcome: "Assessment becomes a process of learning enhancement, not only examination.",
  },
];

export default function Framework() {
  const [active, setActive] = useState("labs");
  const [step, setStep] = useState(0);
  const [ideaPaused, setIdeaPaused] = useState(false);
  const current = PILLARS.find((p) => p.key === active);

  // Auto-rotate the IDEA loop (pauses on user click / hover)
  useEffect(() => {
    if (ideaPaused) return;
    const id = setInterval(() => setStep((s) => (s + 1) % IDEA.length), 7000);
    return () => clearInterval(id);
  }, [ideaPaused]);

  return (
    <div data-testid="framework-page">
      <FloatingQuote />
      <PageHero
        n="A"
        label="PBL-TDLE Framework"
        lines={["THECREATOR", "PBL-TDLE Framework"]}
        intro="Problem-Based Learning (PBL) through Teaching by Demonstrations and Learning by Experiments (TDLE)."
      />

      {/* Innovation callout */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 -mt-4 md:-mt-8 pb-8" data-testid="framework-innovation">
        <Reveal>
          <div className="rounded-2xl p-8 md:p-10 text-white" style={{ background: "linear-gradient(120deg,#0B1B30 0%, #1B4A7A 55%, #2E6BB0 100%)" }}>
            <div className="flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase text-onimg">
              <Sparkles className="w-4 h-4" /> Our Innovation
            </div>
            <p className="text-onimg text-xl md:text-3xl font-display mt-4 leading-snug max-w-4xl">
              PBL-TDLE is an original teaching–learning framework conceived, engineered and proven by THECREATOR Education — moving classrooms from content delivery to genuine problem solving.
            </p>
          </div>
        </Reveal>
      </section>

      {/* From content delivery to problem solving */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <SectionLabel n="A">Problem-Based Learning</SectionLabel>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight">
              From content delivery to problem solving.
            </h2>
            <p className="text-white/65 text-lg leading-relaxed mt-6">
              The future belongs to nations that can solve problems — every product, medicine, technology and patent begins with a problem that was solved. So our classrooms make problem-solving the central daily activity, not an add-on after teaching.
            </p>
            <p className="text-white/65 leading-relaxed mt-4" data-testid="pbl-admissions-para-1">
              Also, every leading national and international university admissions test is built entirely around <span className="text-white">problem-solving in the STEM subjects</span> — Physics, Chemistry, Biology and Mathematics. Their goal is to measure a student's ability to solve problems, not to reproduce facts.
            </p>
            <p className="text-white/65 leading-relaxed mt-4" data-testid="pbl-admissions-para-2">
              The world's top universities also give strong preference to students who qualify high-level academic examinations — <span className="text-white">IPhO, IChO, IBO, IMO, AP, AMC, BPhO</span> and similar. Even NEET and JEE are, at their core, completely problem-based exams.
            </p>
            <p className="text-white/65 leading-relaxed mt-4" data-testid="pbl-admissions-para-3">
              A student who wants to compete for these opportunities must therefore be trained in problem solving — and that is exactly what <span className="text-accent font-semibold">PBL-TDLE</span> is designed to build.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              A <span className="text-accent">real problem</span> is a novel situation whose solution path isn't obvious. Students must:
            </p>
            <ul className="mt-4 space-y-2">
              {["Understand the situation", "Identify given information & required outcome", "Select and connect relevant concepts", "Apply them systematically", "Evaluate and justify the solution"].map((s) => (
                <li key={s} className="flex items-start gap-3 text-white/70">
                  <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0 mt-0.5" /> {s}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="framework-hero-video">
              <YouTubeEmbed id="AyjC3_iyaK8" title="PBL-TDLE Framework — Problem-Based Learning" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* How PBL-TDLE happens */}
      {/* D · How PBL-TDLE develops problem-solvers — the core mechanism */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="framework-how-pbl-tdle">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">How PBL-TDLE Happens</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-5xl font-light">
            Students develop <span className="text-accent">problem-solving skills</span> in STEM using problems — <em>unfamiliar situations</em> in Physics, Chemistry, Biology and Mathematics.
          </h2>

          {/* Two-track flow: TDLE (bottom → concepts) fuels PBL (top → problems) */}
          <div className="mt-16 grid lg:grid-cols-12 gap-8 items-stretch">

            {/* Left track — PBL (Problem-Based Learning) */}
            <Reveal className="lg:col-span-7 rounded-2xl border border-accent/40 bg-[#050B14] p-6 md:p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl opacity-20 bg-accent" />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">PBL · Problem-Based Learning</div>
                </div>
                <p className="text-white/70 leading-relaxed mt-3">
                  To solve a problem — an unfamiliar situation — a student must analyse it, understand it and then apply the right concepts (ideas, principles and laws) to arrive at a solution.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-4 gap-3">
                  {[
                    { icon: Puzzle,       t: "Problem",         d: "An unfamiliar STEM situation." },
                    { icon: Search,       t: "Analyse",         d: "Break it down. Understand what is given, what is asked." },
                    { icon: Lightbulb,    t: "Apply Concepts",  d: "Bring in the ideas, principles and laws that fit." },
                    { icon: Target,       t: "Solve",           d: "Reach a verifiable, reasoned solution." },
                  ].map((s, i, arr) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.t} className="relative">
                        <div className="rounded-xl border border-white/10 bg-[#070d18] p-4 h-full">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center border border-accent/40 bg-accent/10 mb-3">
                            <Icon className="w-5 h-5 text-accent" />
                          </div>
                          <div className="font-display text-lg text-white tracking-tight">{s.t}</div>
                          <p className="text-white/60 text-sm mt-1 leading-snug">{s.d}</p>
                        </div>
                        {i < arr.length - 1 && (
                          <ArrowRight className="hidden sm:block absolute top-1/2 -right-2 -translate-y-1/2 w-4 h-4 text-accent" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            {/* Right track — the "engine": TDLE, feeding concepts into PBL */}
            <Reveal delay={0.1} className="lg:col-span-5 rounded-2xl border border-white/10 bg-[#050B14] p-6 md:p-8 relative overflow-hidden">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-c-teal">TDLE · The Concept Engine</div>
              <h3 className="font-display text-2xl md:text-3xl text-white mt-3 tracking-tight leading-snug">
                Concepts are <span className="text-c-teal">developed</span> — not just explained.
              </h3>
              <p className="text-white/70 leading-relaxed mt-4">
                In TDLE, students investigate and verify each concept by performing experiments designed with scientific apparatus. This is the only way a concept truly develops so that it can later be <span className="text-white">applied</span> to solve problems.
              </p>

              <div className="mt-6 rounded-xl border border-white/10 bg-[#070d18] p-4">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-white/60">During investigation, students perform</div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {[
                    { icon: Ruler,       t: "Measurements" },
                    { icon: Calculator,  t: "Calculations" },
                    { icon: LineChart,   t: "Graphing" },
                    { icon: Brain,       t: "Data Analysis" },
                    { icon: CheckCircle2,t: "Conclusions" },
                    { icon: Sparkles,    t: "Inferences" },
                  ].map((a) => {
                    const Icon = a.icon;
                    return (
                      <div key={a.t} className="flex items-center gap-2 text-white/80 text-sm">
                        <Icon className="w-4 h-4 text-c-teal shrink-0" />
                        <span>{a.t}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </div>

          {/* The engine → the problem-solving flow */}
          <div className="mt-10 flex items-center justify-center">
            <Reveal className="flex items-center gap-3 text-white/60">
              <span className="font-mono-label text-[11px] tracking-label uppercase">Developed concepts</span>
              <ArrowRight className="w-4 h-4 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase">Fuel every step of problem-solving</span>
            </Reveal>
          </div>

          {/* The one-line principle */}
          <Reveal delay={0.05} className="mt-16 border-l-4 border-accent bg-[#050B14] rounded-2xl p-8 md:p-10 max-w-4xl">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">The Principle</div>
            <p className="font-display text-2xl md:text-4xl text-white mt-4 leading-tight tracking-tight">
              Concepts must be <span className="text-accent">developed</span> so that they can be <span className="text-accent">applied</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* E · Concepts make problem-solving possible — 8-step PBL-TDLE learning sequence (moved from Concept Labs) */}
      <section className="border-t border-white/10" data-testid="framework-cycle">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="C">Concepts Make Problem-Solving Possible</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            The central learning sequence of <span className="text-accent">PBL-TDLE</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Every PBL-TDLE lesson follows the same eight-step sequence — from a diagnostic that maps the class's concepts, to a solution that each student verifies for themselves. Problem-solving stops being a mystery and becomes a repeatable, evidence-based process.
          </p>

          <ol className="mt-16 relative" data-testid="framework-cycle-steps">
            {/* Vertical accent rail */}
            <div className="hidden md:block absolute left-8 top-4 bottom-4 w-px bg-gradient-to-b from-accent/60 via-white/15 to-accent/60" />

            {CYCLE_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal
                  key={s.title}
                  delay={i * 0.05}
                  className="relative md:pl-24 pb-8 last:pb-0"
                  data-testid={`framework-cycle-step-${i + 1}`}
                >
                  <div
                    className="hidden md:flex absolute left-0 top-1 items-center justify-center w-16 h-16 rounded-full border-2 bg-[#050B14] shadow-xl z-10"
                    style={{ borderColor: s.hex }}
                  >
                    <span className="font-display text-xl text-white">{String(i + 1).padStart(2, "0")}</span>
                  </div>

                  <div className="relative rounded-2xl border border-white/10 bg-[#070d18] p-6 md:p-7 hover:border-accent/60 transition-colors overflow-hidden group">
                    <div
                      className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity"
                      style={{ backgroundColor: s.hex }}
                    />
                    <div className="relative flex items-start gap-5">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center border shrink-0"
                        style={{ borderColor: s.hex, backgroundColor: `${s.hex}22` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: s.hex }} />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="font-mono-label text-[11px] tracking-label uppercase md:hidden"
                          style={{ color: s.hex }}
                        >
                          Step {String(i + 1).padStart(2, "0")}
                        </div>
                        <h3 className="font-display text-xl md:text-2xl text-white mt-1 md:mt-0 tracking-tight leading-snug">
                          {s.title}
                        </h3>
                        <p className="text-white/75 leading-relaxed mt-3">{s.d}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </ol>

          <p className="text-white/70 leading-relaxed mt-14 max-w-3xl">
            The teacher first demonstrates each concept with real apparatus. Students then investigate and verify every concept themselves. Once developed, they apply the concept to the <span className="text-white">curriculum-based, advanced, real-life and open-ended problems</span> that appear on their screens.
          </p>
          <p className="text-white/60 leading-relaxed mt-4 max-w-3xl italic">
            Problem-solving becomes the <span className="not-italic text-white">application of developed concepts</span> — not the mechanical use of memorised formulas.
          </p>
        </div>
      </section>

      {/* Components diagram */}
      <section className="border-t border-white/10 bg-cream text-ink">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-6">
            <SectionLabel n="D" dark>Framework Components</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-ink mt-6 tracking-tight leading-tight font-light">
              A single, coherent architecture for problem-based learning.
            </h2>
            <p className="text-ink/60 mt-6 leading-relaxed">
              Concept Labs supply the apparatus and experiments. The CoPE-App orchestrates the daily academic plan. IDEA pedagogy keeps learning continuously assessed and corrected — together forming one integrated engine.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="aspect-video overflow-hidden rounded-2xl border border-ink/10 bg-black" data-testid="framework-components-video">
              <YouTubeEmbed id="1TmD64vvmg8" title="Framework Components — PBL-TDLE architecture" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Three-pillar interactive */}
      <section className="border-t border-white/10 bg-[#070d18]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="E">Three Pillars</SectionLabel>
          <div className="flex flex-col lg:flex-row gap-4 mt-12 lg:h-[460px]">
            {PILLARS.map((p) => {
              const isOpen = active === p.key;
              const Icon = p.icon;
              return (
                <motion.button
                  key={p.key}
                  onMouseEnter={() => setActive(p.key)}
                  onClick={() => setActive(p.key)}
                  data-testid={`pillar-${p.key}`}
                  animate={{ flexGrow: isOpen ? 3 : 1 }}
                  transition={{ duration: 0.6, ease: [0.83, 0, 0.17, 1] }}
                  className={`relative overflow-hidden text-left border p-8 min-h-[220px] lg:min-h-0 ${isOpen ? "border-accent" : "border-white/10"}`}
                >
                  <div className="absolute inset-0 opacity-20">
                    <img src={p.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="relative h-full flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <Icon className={`w-8 h-8 ${isOpen ? "text-accent" : "text-white/50"}`} />
                      <span className="font-mono-label text-[10px] tracking-label uppercase text-white/40">{p.short}</span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight">{p.title}</h3>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="text-white/70 leading-relaxed mt-4 max-w-xl overflow-hidden"
                          >
                            {p.body}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Concept Labs — real student experiment photography */}
      <section id="concept-labs" className="border-t border-white/10 bg-[#070d18] scroll-mt-24" data-testid="framework-concept-labs">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="F">Concept Labs</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            From classroom to Concept Lab — where students investigate, understand, apply and solve.
          </h2>
          <div className="grid md:grid-cols-12 gap-12 mt-12 items-start">
            <div className="md:col-span-6 space-y-5 text-white/75 leading-relaxed">
              <p>One of the fundamental innovations of PBL-TDLE is the transformation of conventional classrooms into <span className="text-accent font-semibold">Concept Labs</span> — where learning shifts from passive listening to active investigation and problem solving.</p>
              <p>Concept Labs are equipped with several hundred global-standard scientific apparatus and several thousand carefully designed experiments spanning from Upper Kindergarten (UKG) to Grade 12. Students investigate, observe, measure, analyse, interpret and verify scientific and mathematical concepts through direct experience.</p>
              <p>For the secondary section, dedicated Concept Labs are available separately for Physics, Chemistry, Biology and Mathematics. In the primary section, every classroom itself functions as a Concept Lab — a fully equipped space where students continuously learn through demonstrations, experiments, observations, measurements and problem solving.</p>
              <p className="text-white">Hundreds of apparatus. Thousands of experiments. Every concept. Every student.</p>
            </div>
            <div className="md:col-span-6 grid grid-cols-1 gap-4" data-testid="concept-lab-videos">
              {[
                { id: "pJD2xkPp8lY", label: "Concept Lab in action" },
                { id: "MskfyTC6xCE", label: "Students investigating a concept" },
              ].map((v, i) => (
                <Reveal key={v.id} delay={i * 0.08} className="aspect-video overflow-hidden rounded-xl border border-white/10 bg-black">
                  <YouTubeEmbed id={v.id} title={v.label} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="cope-app" className="border-t border-white/10 bg-[#070d18] scroll-mt-24" data-testid="cope-app-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="G">The CoPE-App · Our Smart Lesson-Plan Engine</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl">
            An AI-powered academic engine — one CoPE-App per unit, per subject, per class.
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mt-6 max-w-3xl">
            The CoPE-App is our proprietary cloud lesson-plan engine. For every unit in Physics, Chemistry, Biology and Mathematics — and for every class from Grade 0 (Upper KG) to Grade 12 — the platform generates a purpose-built <span className="text-accent font-semibold">CoPE-App</span> that bundles the four things a teacher actually needs to run a Concept Lab.
          </p>

          {/* Live platform link — Teacher & Student portals */}
          <div className="mt-8 rounded-2xl border border-white/10 bg-[#050B14] p-6 md:p-7 max-w-4xl" data-testid="cope-platform-link">
            <div className="flex items-start md:items-center gap-4 md:gap-6 flex-col md:flex-row">
              <div className="flex-1">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Live Platform · Teacher &amp; Student Portals</div>
                <p className="text-white/85 leading-relaxed mt-2">
                  The CoPE-App is already live for partner schools at <span className="text-white font-semibold">tce-pbl.uk</span> — with dedicated logins for teachers, students and school leaders.
                </p>
              </div>
              <a
                href="https://tce-pbl.uk/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cope-platform-cta"
                className="inline-flex items-center gap-2 bg-accent text-[#050B14] px-5 py-3 rounded-full text-sm font-semibold hover:bg-accent/90 transition-colors shrink-0"
              >
                Open the CoPE-App <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Acronym legend */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10">
            {COPE_LETTERS.map((c) => (
              <div key={c.key} className="border border-white/10 rounded-xl px-4 py-4" data-testid={`cope-legend-${c.key}`}>
                <div className="font-display text-3xl md:text-4xl tracking-tight" style={{ color: c.hex }}>{c.letter}</div>
                <div className="font-mono-label text-[11px] tracking-label uppercase text-white/60 mt-2">{c.tag.split(" — ")[1]}</div>
              </div>
            ))}
          </div>

          {/* Pictorial — how Concepts connect with Apparatus, Experiments & Problems */}
          <Reveal>
            <div className="mt-12 rounded-3xl border dk-border dk-panel overflow-hidden" data-testid="cope-relationship-diagram">
              <div className="p-8 md:p-12 pb-0">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">The CoPE Relationship</div>
                <h3 className="font-display text-2xl md:text-3xl dk-text mt-3 tracking-tight leading-tight max-w-3xl">
                  Concepts are developed by apparatus &amp; experiments — and applied on problems for their solution.
                </h3>
              </div>

              {/* Desktop flow */}
              <div className="hidden lg:grid grid-cols-[1fr_auto_1.15fr_auto_1fr] items-center gap-0 p-12">
                {/* Sources — Apparatus + Experiment */}
                <div className="space-y-5">
                  {[
                    { icon: Wrench, t: "Apparatus", d: "1,500+ global-standard instruments make every concept measurable.", hex: "#B79CF5" },
                    { icon: Beaker, t: "Experiment", d: "Thousands of designed experiments to investigate & verify each concept.", hex: "#38C9C9" },
                  ].map((n) => {
                    const Icon = n.icon;
                    return (
                      <div key={n.t} className="rounded-2xl border dk-border dk-card p-6" data-testid={`diagram-node-${n.t.toLowerCase()}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: n.hex, backgroundColor: `${n.hex}22` }}>
                            <Icon className="w-5 h-5" style={{ color: n.hex }} />
                          </div>
                          <div className="font-display text-xl dk-text tracking-tight">{n.t}</div>
                        </div>
                        <p className="dk-text-muted text-sm leading-relaxed mt-3">{n.d}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Converging arrows — DEVELOP */}
                <div className="relative px-2 w-[130px] self-stretch flex items-center justify-center">
                  <svg viewBox="0 0 130 240" className="w-full h-full absolute inset-0" fill="none" preserveAspectRatio="none">
                    <defs>
                      <marker id="arr-dev" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L6,3 L0,6" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" fill="none" />
                      </marker>
                    </defs>
                    <path d="M4,62 C60,62 70,110 118,118" stroke="rgba(255,255,255,0.4)" strokeWidth="1.6" strokeDasharray="5 5" markerEnd="url(#arr-dev)" />
                    <path d="M4,178 C60,178 70,130 118,122" stroke="rgba(255,255,255,0.4)" strokeWidth="1.6" strokeDasharray="5 5" markerEnd="url(#arr-dev)" />
                  </svg>
                  <span className="relative font-mono-label text-[10px] tracking-label uppercase text-c-teal border rounded-full px-3 py-1.5 dk-panel" style={{ borderColor: "#38C9C9" }}>Develop</span>
                </div>

                {/* Hub — Concept */}
                <div className="rounded-3xl border-2 p-8 text-center relative overflow-hidden" style={{ borderColor: "#6FA8E8", backgroundColor: "rgba(111,168,232,0.10)" }} data-testid="diagram-node-concept">
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-25" style={{ background: "#6FA8E8" }} />
                  <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center border" style={{ borderColor: "#6FA8E8", backgroundColor: "rgba(111,168,232,0.18)" }}>
                    <Lightbulb className="w-7 h-7" style={{ color: "#6FA8E8" }} />
                  </div>
                  <div className="font-display text-3xl dk-text mt-4 tracking-tight">Concept</div>
                  <p className="dk-text-soft text-sm leading-relaxed mt-3">
                    The idea, law or principle — <span className="dk-text">developed through experience</span>, not memorised from a textbook.
                  </p>
                </div>

                {/* Apply arrow */}
                <div className="relative px-2 w-[130px] self-stretch flex items-center justify-center">
                  <svg viewBox="0 0 130 240" className="w-full h-full absolute inset-0" fill="none" preserveAspectRatio="none">
                    <defs>
                      <marker id="arr-app" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path d="M0,0 L6,3 L0,6" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" fill="none" />
                      </marker>
                    </defs>
                    <path d="M4,120 L118,120" stroke="rgba(255,255,255,0.4)" strokeWidth="1.6" strokeDasharray="5 5" markerEnd="url(#arr-app)" />
                  </svg>
                  <span className="relative font-mono-label text-[10px] tracking-label uppercase text-c-amber border rounded-full px-3 py-1.5 dk-panel" style={{ borderColor: "#F0A13C" }}>Apply</span>
                </div>

                {/* Outcome — Problem → Solution */}
                <div className="rounded-2xl border dk-border dk-card p-6" data-testid="diagram-node-problem">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: "#F0A13C", backgroundColor: "rgba(240,161,60,0.16)" }}>
                      <Puzzle className="w-5 h-5" style={{ color: "#F0A13C" }} />
                    </div>
                    <div className="font-display text-xl dk-text tracking-tight">Problem</div>
                  </div>
                  <p className="dk-text-muted text-sm leading-relaxed mt-3">10,000+ real-life, open-ended problems where developed concepts are applied.</p>
                  <div className="mt-5 pt-4 border-t dk-border flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0" />
                    <span className="font-mono-label text-[10px] tracking-label uppercase text-c-teal">Solution — concept mastery proven</span>
                  </div>
                </div>
              </div>

              {/* Mobile flow — vertical */}
              <div className="lg:hidden p-8 space-y-4">
                {[
                  { icon: Wrench, t: "Apparatus", d: "1,500+ global-standard instruments", hex: "#B79CF5" },
                  { icon: Beaker, t: "Experiment", d: "Investigate, measure & verify", hex: "#38C9C9" },
                ].map((n) => {
                  const Icon = n.icon;
                  return (
                    <div key={n.t} className="rounded-2xl border dk-border dk-card p-5 flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: n.hex, backgroundColor: `${n.hex}22` }}>
                        <Icon className="w-5 h-5" style={{ color: n.hex }} />
                      </div>
                      <div>
                        <div className="font-display text-lg dk-text tracking-tight">{n.t}</div>
                        <p className="dk-text-muted text-xs mt-1">{n.d}</p>
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-center gap-2 py-1">
                  <ArrowDown className="w-4 h-4 text-c-teal" />
                  <span className="font-mono-label text-[10px] tracking-label uppercase text-c-teal">Develop</span>
                </div>
                <div className="rounded-2xl border-2 p-6 text-center" style={{ borderColor: "#6FA8E8", backgroundColor: "rgba(111,168,232,0.10)" }}>
                  <Lightbulb className="w-7 h-7 mx-auto" style={{ color: "#6FA8E8" }} />
                  <div className="font-display text-2xl dk-text mt-3 tracking-tight">Concept</div>
                  <p className="dk-text-soft text-sm mt-2">Developed through experience — not memorised.</p>
                </div>
                <div className="flex items-center justify-center gap-2 py-1">
                  <ArrowDown className="w-4 h-4 text-c-amber" />
                  <span className="font-mono-label text-[10px] tracking-label uppercase text-c-amber">Apply</span>
                </div>
                <div className="rounded-2xl border dk-border dk-card p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: "#F0A13C", backgroundColor: "rgba(240,161,60,0.16)" }}>
                      <Puzzle className="w-5 h-5" style={{ color: "#F0A13C" }} />
                    </div>
                    <div>
                      <div className="font-display text-lg dk-text tracking-tight">Problem</div>
                      <p className="dk-text-muted text-xs mt-1">10,000+ real-life problems</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t dk-border flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0" />
                    <span className="font-mono-label text-[10px] tracking-label uppercase text-c-teal">Solution — mastery proven</span>
                  </div>
                </div>
              </div>

              <div className="px-8 md:px-12 py-4 border-t dk-border font-mono-label text-[10px] tracking-label uppercase dk-text-muted">
                ★ Every concept in the CoPE-App is linked to its apparatus, its experiments and its problems ★
              </div>
            </div>
          </Reveal>

          {/* Four native CoPE cards */}
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {COPE_LETTERS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.key} delay={(i % 2) * 0.08} className="border border-white/10 rounded-2xl overflow-hidden bg-[#050B14] flex flex-col" data-testid={`cope-card-${c.key}`}>
                  <div className="p-8 md:p-10 border-b border-white/10 relative overflow-hidden">
                    <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full opacity-10 blur-2xl" style={{ background: c.hex }} />
                    <div className="flex items-start gap-5">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: c.hex, backgroundColor: `${c.hex}22` }}>
                        <Icon className="w-7 h-7" style={{ color: c.hex }} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: c.hex }}>{c.tag}</div>
                        <h3 className="font-display text-2xl md:text-3xl text-white mt-2 tracking-tight leading-tight">{c.title}</h3>
                        <p className="text-white/60 text-sm mt-2">{c.subtitle}</p>
                      </div>
                    </div>
                    <p className="text-white/75 leading-relaxed mt-6">{c.body}</p>
                  </div>
                  <div className="p-6 md:p-8 grid grid-cols-2 gap-4 flex-1">
                    {c.steps.map((s) => (
                      <div key={s.n} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors">
                        <div className="font-mono-label text-[10px] tracking-label uppercase text-white/40">{s.n}</div>
                        <div className="font-display text-lg text-white mt-1" style={{ color: c.hex }}>{s.t}</div>
                        <p className="text-white/55 text-xs leading-relaxed mt-1">{s.d}</p>
                      </div>
                    ))}
                  </div>
                  <div className="px-8 md:px-10 py-4 border-t border-white/10 font-mono-label text-[11px] tracking-label uppercase" style={{ color: c.hex }}>
                    ★ {c.ribbon} ★
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Subject × Class matrix — a CoPE-App for every cell */}
          <div className="mt-14 rounded-2xl border border-white/10 overflow-hidden" data-testid="cope-matrix">
            <div className="p-6 md:p-8 border-b border-white/10 flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Coverage Matrix</div>
                <h3 className="font-display text-2xl md:text-3xl text-white mt-2 tracking-tight leading-tight">
                  A CoPE-App for every unit — every subject × every class.
                </h3>
                <p className="text-white/60 leading-relaxed mt-3 max-w-2xl">
                  4 subjects × 12 grades × dozens of units per grade — every cell has its own CoPE-App, pre-loaded with the concepts, problems, experiments and apparatus for that specific unit.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 border border-accent/40 rounded-full px-4 py-2 font-mono-label text-[11px] tracking-label uppercase text-accent bg-accent/5">
                <Layers className="w-4 h-4" /> 48 subject-grade tracks
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-3 font-mono-label text-[10px] tracking-label uppercase text-white/45 border-b border-white/10">Subject / Class</th>
                    {COPE_GRADES.map((g) => (
                      <th key={g} className="px-2 py-3 font-mono-label text-[10px] tracking-label uppercase text-white/45 border-b border-white/10 text-center">{g}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COPE_SUBJECTS.map((sub, si) => {
                    const cols = ["#2E6BB0", "#F0A831", "#1E9E8A", "#8B5CF6"];
                    return (
                      <tr key={sub} className={si % 2 ? "bg-[#050B14]" : ""}>
                        <td className="px-4 py-3 font-display text-white whitespace-nowrap border-b border-white/5" style={{ color: cols[si] }}>{sub}</td>
                        {COPE_GRADES.map((g) => (
                          <td key={g} className="px-2 py-3 text-center border-b border-white/5">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cols[si], boxShadow: `0 0 8px ${cols[si]}55` }} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Diagnostic — hero callout */}
          <div className="mt-14 rounded-3xl border border-white/10 overflow-hidden" style={{ background: "linear-gradient(120deg,#0B1B30 0%, #1B4A7A 55%, #2E6BB0 100%)" }} data-testid="cope-ai-diagnostic">
            <div className="p-8 md:p-14">
              <div className="inline-flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase rounded-full px-4 py-2" style={{ color: "#ffffff", backgroundColor: "rgba(255,255,255,0.18)" }}>
                <Brain className="w-4 h-4" /> AI-Based Diagnostic Engine
              </div>
              <h3 className="font-display text-3xl md:text-5xl mt-6 tracking-tight leading-tight max-w-4xl" style={{ color: "#ffffff" }}>
                The AI that reads, analyzes and maps the cohort — before the teacher begins the lesson.
              </h3>
              <p className="text-lg leading-relaxed mt-6 max-w-3xl" style={{ color: "rgba(255,255,255,0.88)" }}>
                Sitting behind the CoPE-App is an AI-based diagnostic system that maps every student's understanding of every concept — surfacing strengths, gaps and misconceptions across the whole cohort <span style={{ color: "#ffffff", fontWeight: 600 }}>before teaching even begins</span>. The next lesson is then written to fix precisely what the data says.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                {AI_DIAGNOSTIC_CAPS.map((cap, i) => {
                  const Icon = cap.icon;
                  return (
                    <Reveal key={cap.t} delay={i * 0.06} className="rounded-2xl border border-white/15 p-6" style={{ backgroundColor: "rgba(255,255,255,0.06)", backdropFilter: "blur(6px)" }} data-testid={`ai-cap-${i}`}>
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center border" style={{ borderColor: cap.hex, backgroundColor: `${cap.hex}33` }}>
                        <Icon className="w-6 h-6" style={{ color: cap.hex }} />
                      </div>
                      <h4 className="font-display text-lg mt-5 tracking-tight leading-snug" style={{ color: "#ffffff" }}>{cap.t}</h4>
                      <p className="leading-relaxed mt-3 text-sm" style={{ color: "rgba(255,255,255,0.82)" }}>{cap.d}</p>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Levels & audience moved to IDEA Pedagogy and elsewhere */}

          <Reveal>
            <p className="text-white/60 leading-relaxed mt-10 max-w-3xl">
              <span className="text-accent font-medium">Teacher-independent, system-driven learning.</span> The CoPE-App creates a fully integrated ecosystem — connecting teachers, students, parents and school leaders on a single platform — ensuring complete transparency, continuous formative assessment, concept mastery and problem-solving excellence for every student.
            </p>
          </Reveal>
        </div>
      </section>

      {/* IDEA Pedagogy — Levels first, then the loop */}
      <section className="border-t border-white/10" data-testid="framework-idea">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="H">IDEA Pedagogy — the loop</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Three tiers of problem solving — Foundation, Analytical, Olympiad-grade.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {COPE_LEVELS.map((l, i) => {
              const colors = ["text-c-blue", "text-c-amber", "text-c-rose"];
              return (
                <Reveal key={l.lvl} delay={i * 0.08} className="border border-white/10 rounded-xl p-8 bg-[#050B14]" data-testid={`idea-level-${i}`}>
                  <span className={`font-mono-label text-[11px] tracking-label uppercase ${colors[i]}`}>{l.lvl}</span>
                  <h3 className="font-display text-2xl text-white mt-3">{l.t}</h3>
                  <p className="text-white/55 leading-relaxed mt-3 text-sm">{l.d}</p>
                </Reveal>
              );
            })}
          </div>
          {/* IDEA loop — 4 auto-cycling panels in one row */}
          <div className="mt-16" data-testid="idea-loop">
            <div className="grid grid-cols-4 gap-3 md:gap-4">
              {IDEA.map((s, i) => (
                <button
                  key={s.n}
                  type="button"
                  onClick={() => { setStep(i); setIdeaPaused(true); }}
                  onMouseEnter={() => setIdeaPaused(true)}
                  onMouseLeave={() => setIdeaPaused(false)}
                  data-testid={`idea-step-${i}`}
                  aria-pressed={step === i}
                  className={`relative aspect-square rounded-2xl border-2 flex flex-col items-center justify-center transition-all duration-500 overflow-hidden ${
                    step === i
                      ? "bg-accent text-[#050B14] border-accent shadow-2xl shadow-accent/30 scale-[1.02]"
                      : "border-white/15 text-white/80 hover:border-white/40 bg-[#050B14]"
                  }`}
                >
                  {/* Progress ring on the active panel */}
                  {step === i && !ideaPaused && (
                    <span
                      key={`ring-${step}`}
                      aria-hidden
                      className="absolute inset-0 rounded-2xl border-2 border-white/40 pointer-events-none animate-idea-ring"
                    />
                  )}
                  <span className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-light">{s.n}</span>
                  <span className="font-mono-label text-[9px] sm:text-[10px] md:text-[11px] tracking-label uppercase mt-1 md:mt-2 px-2 text-center leading-tight">{s.t}</span>
                </button>
              ))}
            </div>

            {/* Interaction hint */}
            <div className="mt-3 flex items-center justify-center gap-3 text-white/40" data-testid="idea-hint">
              <span className="font-mono-label text-[10px] tracking-label uppercase">Hover to pause</span>
              <span className="text-white/20">·</span>
              <span className="font-mono-label text-[10px] tracking-label uppercase">Click to jump</span>
            </div>

            {/* Description panel — animated on step change */}
            <div className="mt-10 rounded-2xl border border-white/10 bg-[#050B14] p-8 md:p-10 min-h-[320px]" data-testid="idea-description">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono-label text-[11px] tracking-label uppercase text-accent">Step 0{step + 1} · Letter {IDEA[step].n}</span>
                    <span className="hidden sm:inline-flex gap-1.5 ml-2">
                      {IDEA.map((_, i) => (
                        <span
                          key={i}
                          className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? "bg-accent" : "bg-white/20"}`}
                        />
                      ))}
                    </span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-white mt-3 tracking-tight font-light">{IDEA[step].t}</h3>
                  <p className="text-white/75 text-base md:text-lg leading-relaxed mt-5 max-w-4xl">{IDEA[step].lede}</p>

                  <div className={`grid gap-4 mt-8 ${IDEA[step].bullets.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
                    {IDEA[step].bullets.map((b, i) => (
                      <div key={b.h} className="rounded-xl border border-white/10 bg-[#070d18] p-5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono-label text-[10px] tracking-label uppercase text-accent">{String(i + 1).padStart(2, "0")}</span>
                          <span className="font-display text-lg text-white tracking-tight">{b.h}</span>
                        </div>
                        <p className="text-white/60 text-sm leading-relaxed mt-2">{b.d}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 inline-flex items-center gap-2 border-l-2 border-accent pl-4">
                    <span className="font-mono-label text-[10px] tracking-label uppercase text-accent">Outcome</span>
                    <span className="text-white/80 text-sm md:text-base leading-snug">{IDEA[step].outcome}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* I · Formative Continuous Assessment */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="framework-fca">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="I">Formative Continuous Assessment in PBL-TDLE</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Assessment as an <span className="text-accent">integral part of learning</span> — not an exam after it.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            In the PBL-TDLE Framework, assessment is not treated as a separate examination conducted after teaching has been completed. It is integrated into the learning process through <span className="text-white font-medium">Formative Continuous Assessment (FCA)</span> during every stage of IDEA pedagogy. At every stage, the teacher observes and evaluates each student's work, assigns marks immediately and provides feedback on the spot — rewarding participation, effort, independent attempts and self-correction.
          </p>
          <p className="text-white/70 leading-relaxed mt-4 max-w-3xl">
            The purpose of FCA is not merely to record marks. Its primary purpose is to <span className="text-white">identify learning gaps, encourage every student to attempt the work, correct misconceptions</span> and help learners progress towards concept mastery and independent problem-solving.
          </p>

          {/* IDEA chain */}
          <Reveal>
            <div className="mt-10 rounded-2xl border dk-border dk-panel p-6 md:p-8" data-testid="fca-idea-chain">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">IDEA — Four Connected Stages</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-3 mt-4">
                {["Identify the Problem", "Develop the Concepts", "Execute the Problem", "Assess the Learning"].map((s, i, arr) => (
                  <span key={s} className="inline-flex items-center gap-3">
                    <span className="font-display text-lg md:text-2xl dk-text tracking-tight">{s}</span>
                    {i < arr.length - 1 && <ArrowRight className="w-5 h-5 text-c-teal shrink-0" />}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          {/* FCA1 */}
          <div className="mt-16" data-testid="fca1-block">
            <div className="flex items-center gap-3">
              <span className="font-display text-4xl text-accent leading-none">FCA1</span>
              <span className="font-mono-label text-[11px] tracking-label uppercase text-white/60">Daily · Continuous · Real-Time Assessment</span>
            </div>
            <p className="text-white/70 leading-relaxed mt-4 max-w-3xl">
              FCA1 takes place every day during the actual classroom learning process — following the student through every stage of problem identification, demonstration, experimentation, execution and self-correction.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mt-10">
              {[
                { n: "01", t: "Identifying the Problem", hex: "#2AA7D4", intro: "Students first examine the situation presented. The teacher assesses whether they can:", items: ["Understand the given situation", "Identify the central problem", "Recognise the information provided", "Determine what needs to be investigated", "Recall relevant prior knowledge", "Ask appropriate questions"], note: "Marks are awarded for participation and the ability to identify and understand the problem. Students who misunderstand are guided to reconsider and correct their interpretation before proceeding." },
                { n: "02", t: "Developing the Concepts", hex: "#1E9E8A", intro: "Concepts are introduced through demonstrations, apparatus and guided discussion, then developed and verified by students performing experiments themselves. The teacher assesses:", items: ["Participation in the demonstration and experiment", "Understanding of the concept being investigated", "Correct handling and use of apparatus", "Observation and measurement skills", "Accuracy of recorded information", "Analysis and interpretation of results", "Connecting experimental evidence with the concept", "Drawing appropriate conclusions"], note: "Students receive immediate feedback while the activity is still taking place — errors are reviewed, identified and corrected under the teacher's guidance, with marks awarded at every stage." },
                { n: "03", t: "Executing the Problem", hex: "#F0A831", intro: "Students independently attempt the solution to the given problem. They are expected to:", items: ["Select the concepts they believe are relevant", "Decide how those concepts should be applied", "Develop their own method or strategy", "Organise the solution into logical steps", "Perform the required calculations", "Record their reasoning and final answer", "Complete the solution independently"], note: "The teacher rewards a genuine attempt with marks even if the final solution is incorrect — encouraging independent thinking without fear of losing marks. Students must construct their own solution before seeing the standard one." },
                { n: "04", t: "Assessing the Learning", hex: "#8B5CF6", intro: "The teacher displays and explains the standard solution. Students compare their own work step by step, examining:", items: ["Whether they selected the correct concepts", "Whether each concept was applied appropriately", "Whether the method was logically organised", "Whether any important step was missing", "Whether calculations were accurate", "Where their reasoning differed — and why", "How their solution could be improved"], note: "Students correct their own work wherever they find an error. The teacher checks the corrected solution and awards additional marks for successful self-assessment and self-correction — making assessment an active learning process." },
              ].map((c, i) => (
                <Reveal key={c.t} delay={(i % 2) * 0.08}>
                  <div className="h-full border border-white/10 rounded-2xl bg-[#050B14] p-7 md:p-8" data-testid={`fca1-stage-${i + 1}`}>
                    <div className="flex items-center gap-4">
                      <span className="font-display text-4xl leading-none" style={{ color: c.hex }}>{c.n}</span>
                      <h3 className="font-display text-xl md:text-2xl text-white tracking-tight">{c.t}</h3>
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed mt-4">{c.intro}</p>
                    <ul className="mt-4 space-y-2">
                      {c.items.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: c.hex }} /> {b}
                        </li>
                      ))}
                    </ul>
                    <p className="text-white/55 text-sm leading-relaxed mt-5 pt-4 border-t border-white/10">{c.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Marks for attempt + self-correction */}
            <Reveal>
              <div className="mt-10 rounded-2xl border dk-border dk-panel p-8 md:p-10" data-testid="fca-marks-panel">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">Marks for Attempt · Marks for Self-Correction</div>
                <div className="grid md:grid-cols-2 gap-8 mt-6">
                  <div>
                    <div className="font-display text-xl dk-text tracking-tight">Marks for Independent Attempt</div>
                    <p className="dk-text-soft text-sm leading-relaxed mt-3">During execution, students receive marks for attempting the problem independently — recognising effort, reasoning, concept selection and the willingness to solve, even when the final solution is incorrect.</p>
                  </div>
                  <div>
                    <div className="font-display text-xl dk-text tracking-tight">Marks for Self-Correction</div>
                    <p className="dk-text-soft text-sm leading-relaxed mt-3">During assessment, students receive further marks for comparing their work with the standard solution, identifying errors and correcting themselves — mistakes become part of learning, not failure.</p>
                  </div>
                </div>
                <div className="mt-8 pt-6 border-t dk-border">
                  <div className="font-mono-label text-[10px] tracking-label uppercase dk-text-muted mb-4">The Complete Process</div>
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                    {["Independent attempt", "Marks for attempt", "Standard solution displayed", "Step-by-step comparison", "Errors identified", "Self-correction", "Teacher checks", "Marks for self-correction"].map((s, i, arr) => (
                      <span key={s} className="inline-flex items-center gap-2.5">
                        <span className="dk-text-soft text-sm">{s}</span>
                        {i < arr.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-c-teal shrink-0" />}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Learner App + analytics + parents */}
            <div className="grid md:grid-cols-3 gap-6 mt-10" data-testid="fca-realtime-grid">
              {[
                { icon: Cloud, hex: "#2AA7D4", t: "Real-Time Recording · Learner App", d: "FCA1 marks are entered into the Learner App in real time, continuously updating every student's academic performance throughout the school day — capturing participation, independent attempts, experimental work, concept application and self-correction.", chips: ["Class-wise", "Subject-wise", "Period-wise", "Unit-wise", "Concept-wise", "Experiment-wise", "Problem-wise", "Student-wise"] },
                { icon: LineChart, hex: "#F0A831", t: "Live Analytics for School Leadership", d: "Leaders and academic coordinators see participation, mastery, difficult problems, students needing support and progress after correction — across classes and periods. Intervention happens while the unit is still being learned, not after a later examination.", chips: null },
                { icon: Users2, hex: "#1E9E8A", t: "Real-Time Information for Parents", d: "Parents view their child's performance in real time — participation, experimental work, independent problem-solving attempts, concept application, self-correction and assessment outcomes — not just occasional report cards.", chips: null },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.t} delay={i * 0.08}>
                    <div className="h-full border border-white/10 rounded-2xl bg-[#050B14] p-7" data-testid={`fca-realtime-${i}`}>
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: c.hex, backgroundColor: `${c.hex}18` }}>
                        <Icon className="w-5 h-5" style={{ color: c.hex }} />
                      </div>
                      <div className="font-display text-lg text-white mt-5 tracking-tight leading-snug">{c.t}</div>
                      <p className="text-white/65 text-sm leading-relaxed mt-3">{c.d}</p>
                      {c.chips && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {c.chips.map((ch) => (
                            <span key={ch} className="font-mono-label text-[9px] tracking-label uppercase border border-white/15 rounded-full px-2.5 py-1 text-white/60">{ch}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* FCA2 */}
          <div className="mt-16" data-testid="fca2-block">
            <div className="flex items-center gap-3">
              <span className="font-display text-4xl text-accent leading-none">FCA2</span>
              <span className="font-mono-label text-[11px] tracking-label uppercase text-white/60">Unit-End Concept Application & Presentation</span>
            </div>
            <p className="text-white/70 leading-relaxed mt-4 max-w-3xl">
              While FCA1 evaluates learning continuously, FCA2 assesses whether students can independently apply the concepts developed throughout the unit. Students solve carefully selected problems as a home assignment and present their solutions before their peers and teacher.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <Reveal>
                <div className="h-full border border-white/10 rounded-2xl bg-[#050B14] p-7 md:p-8">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">During the Presentation, Each Student Must</div>
                  <ul className="mt-4 space-y-2">
                    {["Explain the problem clearly", "Identify the concepts required", "Describe why each concept is relevant", "Explain how each concept is applied", "Present every step of the solution", "Justify calculations and reasoning", "Interpret the final result", "Respond to questions from teacher and peers", "Defend or revise the solution when required"].map((b) => (
                      <li key={b} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0 mt-0.5" /> {b}
                      </li>
                    ))}
                  </ul>
                  <p className="text-white/55 text-sm leading-relaxed mt-5 pt-4 border-t border-white/10">
                    FCA2 does not assess the final answer alone — it evaluates the complete thinking process. Peer discussion lets other students examine different methods, raise questions and compare approaches, strengthening both presenter and audience.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="h-full border border-white/10 rounded-2xl bg-[#050B14] p-7 md:p-8">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Mastery Beyond Written Answers — FCA2 Strengthens</div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {["Concept mastery", "Independent thinking", "Concept application", "Analytical reasoning", "Problem-solving ability", "Scientific communication", "Presentation skills", "Confidence in public explanation", "Ability to answer questions", "Peer learning & discussion"].map((b) => (
                      <div key={b} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed border border-white/10 rounded-xl px-3 py-2.5">
                        <Sparkles className="w-3.5 h-3.5 text-c-amber shrink-0 mt-1" /> {b}
                      </div>
                    ))}
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mt-5 pt-4 border-t border-white/10">
                    Regular presentation prepares students to communicate their ideas confidently in examinations, competitions, interviews, research discussions and professional situations.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Closing — assessment FOR learning */}
          <Reveal>
            <div className="mt-16 rounded-3xl border-2 dk-panel relative overflow-hidden p-10 md:p-14" style={{ borderColor: "rgba(42,167,212,0.5)" }} data-testid="fca-closing">
              <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
              <div className="relative">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">From Assessment of Learning → to Assessment for Learning</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-3 mt-6">
                  {["Participation", "Independent Attempt", "Assessment", "Comparison", "Self-Correction", "Reassessment", "Concept Mastery", "Independent Application"].map((s, i, arr) => (
                    <span key={s} className="inline-flex items-center gap-3">
                      <span className="font-display text-lg md:text-xl dk-text tracking-tight">{s}</span>
                      {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-c-teal shrink-0" />}
                    </span>
                  ))}
                </div>
                <p className="dk-text-soft text-base md:text-lg leading-relaxed mt-8 max-w-4xl">
                  Through FCA, learning no longer remains assumed or invisible. Teachers can verify it, students can improve it, school leaders can analyse it and parents can follow it in real time. Formative Continuous Assessment makes learning <span className="dk-text font-medium">visible, measurable, correctable, verifiable and accountable</span> — while ensuring every student receives repeated opportunities to attempt, learn from mistakes, correct their understanding and achieve mastery in concept application and problem-solving.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Guiding principle */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <SectionLabel n="✦">Guiding Principle</SectionLabel>
          <Reveal>
            <p className="font-display text-2xl md:text-4xl lg:text-5xl text-white/85 leading-[1.15] tracking-tight mt-10 max-w-6xl font-light">
              “PBL-TDLE is based on the fundamental principle that every student who actively engages in learning can develop concepts and become a successful problem solver, irrespective of their inherent talent or starting ability.”
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
